import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { EquationPartsModel } from '~/bootcamp/Activities/Algebra/Algebra.utils'

import { generateUnlikeInputActivityData, ActivityDataModel, checkAnswer } from './UnlikeInput.utils'

interface UseUnlikeInputReturnType {
  equationParts: EquationPartsModel[]
  formValid: boolean
  answer: string
  handleCheckAnswer: () => void
  onInputChange: (value: string) => void
  goToNextQuestion: () => void
  inputRef: RefObject<HTMLInputElement>
}

interface UseUnlikeInputProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
  autofocus: boolean
  answerState: boolean | null
}

function UseUnlikeInput({
  onAnswerStateChange,
  onNextQuestionClick,
  difficultyLevel,
  autofocus,
  answerState,
}: UseUnlikeInputProps): UseUnlikeInputReturnType {
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [answer, setAnswer] = useState<string>('')
  const [activityData, setActivityData] = useState<ActivityDataModel>({
    equationString: '',
    originalEquationString: '',
    equationParts: [],
    maxHistoryLength: 10,
  })

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEffect(() => {
    setAnswer('')
    setActivityData(generateUnlikeInputActivityData(difficultyLevel, taskHistory))
  }, [taskHistory, difficultyLevel])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const handleCheckAnswer = useCallback((): void => {
    onAnswerStateChange(checkAnswer(answer, activityData.equationString))
  }, [activityData.equationString, answer, onAnswerStateChange])

  const onInputChange = useCallback(
    (value: string) => {
      if (value === 'clear') {
        setAnswer('')
        onAnswerStateChange(null)
      } else {
        setAnswer(value)
      }
    },
    [onAnswerStateChange],
  )

  const goToNextQuestion = useCallback(() => {
    setTaskHistory((prevState) => [activityData.equationString, ...prevState].slice(0, activityData.maxHistoryLength))
    onNextQuestionClick()
  }, [onNextQuestionClick, activityData.equationString, activityData.maxHistoryLength])

  return {
    equationParts: activityData.equationParts,
    formValid: answer.length > 0,
    answer,
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
    inputRef,
  }
}

export default UseUnlikeInput
